import React from 'react';
import { useSelector } from 'react-redux';
import { useSetWidgetAttribute } from '../../hooks/useSetWidgetAttribute.js';
import Help from '../../../common/Help.jsx';
import DateInput from '../../../common/DateInput.jsx';
import ColorPicker from '../../../common/ColorPicker/DashboardColorPicker.jsx';
import Select, { oldArrToSelectFormatter} from '../../../common/Select.jsx';
import styles from '../../gen2_widgets/base/CommonSettings.module.scss';
import CollapsibleSection from '../../../common/CollapsibleSection.jsx';
import SettingsSection from '../common/SettingsSection.jsx';
import DatasourceValueSettings from '../datasource/DatasourceValueSettings.jsx';
import WidgetTitleSettings from '../WidgetTitleSettings.jsx';
import CommonSlider from '../../../common/CommonSlider.jsx';

export const MIN_FONT = 5;
export const MAX_FONT = 100;
export const IMAGE_DEFAULT_FONT = 50;
export const VALUE_DEFAULT_FONT = 32;
export const UNIT_DEFAULT_FONT = 15;
export const NAME_ITEM_DEFAULT_FONT = 18;
export const DEFAULT_ADDITIONAL_SETTINGS = ["dataset_id", "name_item", "value", "number_unit", "number_precision"];

function ChartSettings ({
    additional_settings = DEFAULT_ADDITIONAL_SETTINGS,
    widgetId,
    interval_options = [],
    column_options = [],
    dataset_type_options = [],
    x_axis_labels_options = [],
    display_labels_on_data_options = [],
    dataset_mode_options = [],
    number_format_options = ["1 000.00", "1 000,00", "1,000.00","1.000,00"],
    show_total_value_tooltip = false,
    calls = [],
    useDataSourceValues = false,
    datasource_props = {},
    disaggregationOptions = () => {return []}
}) {
    const setAttribute = useSetWidgetAttribute(widgetId);
    //const colWidth = Math.ceil(12 / additional_settings.length);
    const widget = useSelector(state => state.dashboard.widgets[widgetId]) || {}

    return additional_settings.length > 0 ? (
      <CollapsibleSection title={I18n.t('content_settings')}>
        <SettingsSection>
        <WidgetTitleSettings widgetId={widgetId} />
        <div className={styles['settings-option']}>
            <label>{I18n.t('legend_text_color_title')}</label>
            <ColorPicker                
                value={widget.settings?.legend_text_color} 
                onChange={(val) => setAttribute("settings.legend_text_color", val)}
            />
        </div>
        {additional_settings.includes("call_id") ? (
          <div className={styles['settings-option']}>
            <label>
              {I18n.t("call")}
              <Select
                value={widget.call_id}
                options={oldArrToSelectFormatter(calls, 'id', 'name')}
                onChange={(v) => setAttribute('settings.call_id', v)}
              />
            </label>
          </div>
        ) : null}
        {additional_settings.includes("start_date") ? (
          <div className={styles['settings-option']}>
            <label className="w-100 col-xs-12">
              {I18n.t("start_date")}
              <DateInput name="start_date" defaultValue={widget.settings.start_date} onChange={(date) => setAttribute("settings.start_date", date)} />
            </label>
          </div>
        ) : null}
        {additional_settings.includes("end_date") ? (
          <div className={styles['settings-option']}>
            <label className="w-100 col-xs-12">
              {I18n.t("end_date")}
              <DateInput name="end_date" defaultValue={widget.settings.end_date} onChange={(date) => setAttribute("settings.end_date", date)} />
            </label>
          </div>
        ) : null}
        {additional_settings.includes("interval") ? (
          <div className={styles['settings-option']}>
            <label className="w-100 col-xs-12">
              {I18n.t("label_unit")}
              <Select
                value={widget.interval}
                onChange={(v) => setAttribute("settings.interval", v)}
                options={[{value: "", label: I18n.t("automatic")}, ...interval_options.map((o) => ({value: o, label: I18n.t(`date_interval_${o}`)}))]}
              />
            </label>
          </div>
        ) : null}
        {additional_settings.includes("columns") ? (
          <div className={styles['settings-option']}>
            <label className="w-100 col-xs-12">
              {I18n.t("columns")}
              <Select
                value={widget.columns}
                onChange={(v) => setAttribute("settings.columns", v)}
                options={column_options.map((o) => ({value: o, label: I18n.t(`column_option_${o}`)}))}
              />
            </label>
          </div>
        ) : null}
        {additional_settings.includes("dataset_type") ? (
          <div className={styles['settings-option']}>
          <label className="w-100 col-xs-12">
            {I18n.t("data_type")}
            <Select
              value={widget.dataset_type}
              onChange={(v) => setAttribute("settings.dataset_type", v)}
              options={dataset_type_options.map((o) => ({value: o, label: I18n.t(`dataset_type_option_${o}`)}))}
            />
          </label>
        </div>
        ) : null}
        {additional_settings.includes("xaxis_labels") &&
          <div className={styles['settings-option']}>
            <label className="w-100 col-xs-12">
              {I18n.t("widget_xaxis_labels")}
              <Select
                value={widget.xaxis_labels}
                onChange={(v) => setAttribute("settings.xaxis_labels", v)}
                options={x_axis_labels_options.map((o) => ({value: o, label: I18n.t(`xaxis_label_option_${o}`)}))}
              />
            </label>
          </div>
        }
        {additional_settings.includes("display_labels_on_data") &&
          <div className={styles['settings-option']}>
            <label className="w-100 col-xs-12">
              {I18n.t("widget_display_labels_on_data")}
              <Select
                value={widget.display_labels_on_data}
                onChange={(v) => setAttribute("settings.display_labels_on_data", v)}
                options={display_labels_on_data_options.map((o) => ({value: o, label: I18n.t(`display_label_on_data_option_${o}`)}))}
              />
            </label>
          </div>
        }
        {additional_settings.includes("dataset_mode") ? (
          <div className={styles['settings-option']}>
            <label className="w-100 col-xs-12">
              {I18n.t("dataset_mode")}
              <Select
                value={widget.dataset_mode}
                onChange={(v) => setAttribute("settings.dataset_mode", v)}
                options={dataset_mode_options.map((o) => ({value: o, label: I18n.t(`dataset_mode_${o}`)}))}
              />              
            </label>
          </div>
        ) : null}
        {additional_settings.includes("disaggregated_by") && widget.columns && widget.columns != "single" ? (
          <div className={styles['settings-option']}>
            <label className="w-100 col-xs-12">
              {I18n.t("disaggregated_by")}
              <Select
                value={widget.disaggregated_by}
                onChange={(v) => setAttribute("settings.disaggregated_by", v)}
                options={[{value: "", label: I18n.t("select_from_list")}, ...oldArrToSelectFormatter(disaggregationOptions(), 'value', 'label')]}
              />    
            </label>
          </div>
        ) : null}
        {additional_settings.includes("number_format") ? (
          <div className={styles['settings-option']}>
            <label>
              {I18n.t("number_format")}
              <Select
                value={widget.number_format}
                onChange={(v) => setAttribute("settings.number_format", v)}
                options={number_format_options.map((o) => ({value: o, label: o}))}
              />   
            </label>
          </div>
        ) : null}
        {additional_settings.includes("stacking_direction") ? (
          <div className={styles['settings-section']}>
              <label className={styles['settings-option']} style={{marginBottom: '4px'}}>
                  {I18n.t('stacking_direction')}
                  <Select
                    options={[{label: I18n.t('vertical'), value: 'vertical'}, {label: I18n.t('horizontal'), value: 'horizontal'}]}
                    value={widget.settings.stacking || 'horizontal'}
                    placeholder={I18n.t('select_from_list')}
                    onChange={(v) => setAttribute('settings.stacking', v)}
                  />
              </label>
          </div>
        ) : null}
        {
          additional_settings.includes('image_fontSize') &&
            <div className={styles['settings-option']}>
              <label>{`${I18n.t('image')} ${I18n.t('normalize_font_size')}`}<Help text={I18n.t('normalize_font_size_help')} /></label>
              <CommonSlider
                value={widget.settings?.image_fontSize || IMAGE_DEFAULT_FONT}
                min={MIN_FONT}
                max={MAX_FONT+100}
                onChange={(value) => setAttribute('settings.image_fontSize', value)}
              />
          </div>
        }
        {
          additional_settings.includes('value_fontSize') &&
            <div className={styles['settings-option']}>
              <label>{`${I18n.t('value')} ${I18n.t('normalize_font_size')}`}<Help text={I18n.t('normalize_font_size_help')} /></label>
              <CommonSlider
                value={widget.settings?.value_fontSize || VALUE_DEFAULT_FONT}
                min={MIN_FONT}
                max={MAX_FONT}
                onChange={(value) => setAttribute('settings.value_fontSize', value)}
              />
          </div>
        }
        {
          additional_settings.includes('unit_fontSize') &&
            <div className={styles['settings-option']}>
              <label>{`${I18n.t('unit')} ${I18n.t('normalize_font_size')}`}<Help text={I18n.t('normalize_font_size_help')} /></label>
              <CommonSlider
                value={widget.settings?.unit_fontSize || UNIT_DEFAULT_FONT}
                min={MIN_FONT}
                max={MAX_FONT}
                onChange={(value) => setAttribute('settings.unit_fontSize', value)}
              />
          </div>
        }
        {
          additional_settings.includes('name_item_fontSize') &&
            <div className={styles['settings-option']}>
              <label>{`${I18n.t('name_item')} ${I18n.t('normalize_font_size')}`}<Help text={I18n.t('normalize_font_size_help')} /></label>
              <CommonSlider
                value={widget.settings?.name_item_fontSize || NAME_ITEM_DEFAULT_FONT}
                min={MIN_FONT}
                max={MAX_FONT}
                onChange={(value) => setAttribute('settings.name_item_fontSize', value)}
              />
          </div>
        }
        {additional_settings.includes("show_total_value") ? (
          <div className={styles['settings-option']}>
            <div className="show-total-value">
              <label>{I18n.t("widget_show_total_value")}</label>
              {show_total_value_tooltip ? <Help text={I18n.t("show_total_value_help")} /> : null}
              <label className="w-100 col-xs-12">
                <input type="radio" name="show_total_value" defaultChecked={!widget.settings.show_total_value || widget.settings.show_total_value !== 'false'} defaultValue="true" onChange={(e) => {setAttribute("settings.show_total_value", e.target.value)}} style={{ display: 'inline', marginRight: '0.5rem' }}/>
                {I18n.t("option_yes")}
              </label>
              <label>
              <input type="radio" name="show_total_value" defaultChecked={widget.settings.show_total_value && widget.settings.show_total_value === 'false'} defaultValue="false" onChange={(e) => setAttribute("settings.show_total_value", e.target.value)} style={{ display: 'inline', marginRight: '0.5rem' }}/>
                {I18n.t("option_no")}
              </label>
            </div>
          </div>
        ) : null}
      {additional_settings.includes("grip_options") ? (
          <>
            <div className={styles['settings-option']}>
              <div>
                <label>{I18n.t("widget_show_grid")}</label>
                <label className="w-100 col-xs-12">
                  <input type="radio" name="show_grid" defaultChecked={widget.settings.show_grid && widget.settings.show_grid == 'true'} defaultValue="true" onChange={(e) => {setAttribute("settings.show_grid", e.target.value)}} style={{ display: 'inline', marginRight: '0.5rem' }}/>
                  {I18n.t("option_yes")}
                </label>
                <label>
                  <input type="radio" name="show_grid" defaultChecked={widget.settings.show_grid && widget.settings.show_grid == 'false'} defaultValue="false" onChange={(e) => setAttribute("settings.show_grid", e.target.value)} style={{ display: 'inline', marginRight: '0.5rem' }}/>
                  {I18n.t("option_no")}
                </label>
              </div>
            </div>
            <div className={styles['settings-option']}>
              <label className="w-100 col-xs-12">
                {I18n.t("grid_line_color")}
              </label>
                <ColorPicker
                  value={widget.settings.grid_line_color}
                  onChange={(v) => setAttribute('settings.grid_line_color', v)}
                />
            </div>
            <div className={styles['settings-option']}>
              <label className="w-100 col-xs-12">
                {I18n.t("grid_stroke_width")}
                <input type="text" className="form-control" pattern="[0-9]*" name="grid_stroke_width" value={widget.grid_stroke_width} onChange={(e) => (e.target.validity.valid ? setAttribute("settings.grid_stroke_width", e.target.value) : e)} />
              </label>
            </div>
          </>
        ) : null}
        {additional_settings.includes("timeline") ? (
        <>
        {["timeline_background_color","elapsed_time_background_color","value_color","label_color", "past_circle_color", 
        "future_circle_color", "past_circle_border_color","future_circle_border_color","past_icon_color","future_icon_color", "subtitle_color"].map((c, i) =>
        <div className={styles['settings-option']} key={c}>
          <label>{I18n.t(`${c}_title`)}</label>
            <ColorPicker value={widget.settings[c]} onChange={(val) => setAttribute(`settings.${c}`, val)} />
        </div>
        )}
        <div className={styles['settings-option']}>
          <div>
            <label>{I18n.t("widget_show_arrow")}</label>
            <div>
              <label>
                <input type="radio" name="show_arrow" defaultChecked={widget.settings.show_arrow && widget.settings.show_arrow == 'true'} defaultValue="true" onChange={(e) => {setAttribute("settings.show_arrow", e.target.value)}} style={{ display: 'inline', marginRight: '0.5rem' }}/>
                {I18n.t("option_yes")}
              </label>
              <label>
                <input type="radio" name="show_arrow" defaultChecked={widget.settings.show_arrow && widget.settings.show_arrow == 'false'} defaultValue="false" onChange={(e) => setAttribute("settings.show_arrow", e.target.value)} style={{ display: 'inline', marginRight: '0.5rem' }}/>
                {I18n.t("option_no")}
              </label>
            </div>
          </div>
        </div>
        </>
        ) : null}
        {
          useDataSourceValues ? <DatasourceValueSettings {...datasource_props} widgetId={widgetId} /> : null
        }
      </SettingsSection>
      </CollapsibleSection>
    ) : null;
}

export default ChartSettings;
